// HKDSE 2020 Paper 1 Knowledge Graph Samples - Complete Set
// Organized by question types with systematic naming

export const hkdse2020Samples_complete = {
  algebra: [
    {
      id: 'hkdse2020_algebra_01',
      problem: "Simplify $\\frac{(mn^{-2})^5}{m^{-4}}$ and express the answer in positive index form.",
      problemType: "algebra_indices",
      difficulty: "intermediate",
      description: "Simplifying expressions with negative indices",
      graphData: {
        nodes: [
          {
            id: "negative_indices",
            name: "Negative Indices",
            type: "concepts",
            description: "Understanding negative indices and their properties",
            value: "a⁻ⁿ = 1/aⁿ",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "power_of_product",
            name: "Power of Product",
            type: "concepts",
            description: "(ab)ⁿ = aⁿbⁿ",
            value: "(mn⁻²)⁵ = m⁵n⁻¹⁰",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "division_of_powers",
            name: "Division of Powers",
            type: "methods",
            description: "aᵐ/aⁿ = aᵐ⁻ⁿ",
            value: "m⁵n⁻¹⁰/m⁻⁴ = m⁹n⁻¹⁰",
            importance: 9,
            x: 200, y: 0, z: 0
          },
          {
            id: "positive_index_form",
            name: "Positive Index Form",
            type: "concepts",
            description: "Converting to positive index form",
            value: "m⁹/n¹⁰",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "negative_indices",
            to: "power_of_product",
            type: "applied_to",
            strength: 9
          },
          {
            from: "power_of_product",
            to: "division_of_powers",
            type: "leads_to",
            strength: 9
          },
          {
            from: "division_of_powers",
            to: "positive_index_form",
            type: "converts_to",
            strength: 8
          }
        ]
      }
    }
  ],
  factorization: [
    {
      id: 'hkdse2020_factorization_01',
      problem: "Factorize: (a) $a^2 + a - 6$ (b) $a^4 + a^3 - 6a^2$",
      problemType: "factorization_polynomials",
      difficulty: "intermediate",
      description: "Factorizing quadratic and quartic polynomials",
      graphData: {
        nodes: [
          {
            id: "quadratic_factorization",
            name: "Quadratic Factorization",
            type: "concepts",
            description: "Factoring a² + a - 6",
            value: "a² + a - 6 = (a + 3)(a - 2)",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "quartic_factorization",
            name: "Quartic Factorization",
            type: "concepts",
            description: "Factoring a⁴ + a³ - 6a²",
            value: "a⁴ + a³ - 6a² = a²(a² + a - 6)",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "common_factor",
            name: "Common Factor",
            type: "methods",
            description: "Extracting common factor a²",
            value: "a² is common factor in (b)",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "factored_form",
            name: "Factored Form",
            type: "concepts",
            description: "Final factored expressions",
            value: "(a) (a+3)(a-2), (b) a²(a+3)(a-2)",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "quadratic_factorization",
            to: "factored_form",
            type: "provides",
            strength: 9
          },
          {
            from: "common_factor",
            to: "quartic_factorization",
            type: "applied_to",
            strength: 9
          },
          {
            from: "quartic_factorization",
            to: "factored_form",
            type: "provides",
            strength: 9
          }
        ]
      }
    }
  ],
  rounding: [
    {
      id: 'hkdse2020_rounding_01',
      problem: "(a) Round $534.7698$ to the nearest hundred. (b) Round $534.7698$ down to one decimal place. (c) Round $534.7698$ to two significant figures.",
      problemType: "rounding_decimal",
      difficulty: "fundamental",
      description: "Different types of rounding operations",
      graphData: {
        nodes: [
          {
            id: "nearest_hundred",
            name: "Nearest Hundred",
            type: "methods",
            description: "Rounding to nearest hundred",
            value: "534.7698 → 500",
            importance: 8,
            x: -200, y: -200, z: 0
          },
          {
            id: "round_down",
            name: "Round Down",
            type: "methods",
            description: "Rounding down to one decimal",
            value: "534.7698 → 534.7",
            importance: 8,
            x: 0, y: -200, z: 0
          },
          {
            id: "significant_figures",
            name: "Significant Figures",
            type: "methods",
            description: "Rounding to 2 significant figures",
            value: "534.7698 → 530",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "decimal_number",
            name: "Decimal Number",
            type: "variables",
            description: "Original number to round",
            value: "534.7698",
            importance: 9,
            x: 0, y: 0, z: 0
          }
        ],
        links: [
          {
            from: "decimal_number",
            to: "nearest_hundred",
            type: "rounded_by",
            strength: 8
          },
          {
            from: "decimal_number",
            to: "round_down",
            type: "rounded_by",
            strength: 8
          },
          {
            from: "decimal_number",
            to: "significant_figures",
            type: "rounded_by",
            strength: 8
          }
        ]
      }
    }
  ],
  ratios: [
    {
      id: 'hkdse2020_ratios_01',
      problem: "Let $a$, $b$ and $c$ be non-zero numbers such that $\\frac{a}{b} = \\frac{6}{7}$ and $3a = 4c$. Find $\\frac{b + 2c}{a + 2b}$.",
      problemType: "ratios_proportion",
      difficulty: "intermediate",
      description: "Working with ratios and proportions",
      graphData: {
        nodes: [
          {
            id: "ratio_ab",
            name: "Ratio a:b",
            type: "concepts",
            description: "Ratio of a to b",
            value: "a/b = 6/7",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "proportion_ac",
            name: "Proportion a:c",
            type: "concepts",
            description: "Proportion between a and c",
            value: "3a = 4c",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "variable_relationships",
            name: "Variable Relationships",
            type: "methods",
            description: "Expressing variables in terms of one variable",
            value: "a = 6k, b = 7k, c = 4.5k",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "target_expression",
            name: "Target Expression",
            type: "concepts",
            description: "Expression to be evaluated",
            value: "(b + 2c)/(a + 2b)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "ratio_ab",
            to: "variable_relationships",
            type: "provides_constraint",
            strength: 9
          },
          {
            from: "proportion_ac",
            to: "variable_relationships",
            type: "provides_constraint",
            strength: 9
          },
          {
            from: "variable_relationships",
            to: "target_expression",
            type: "evaluates",
            strength: 9
          }
        ]
      }
    }
  ],
  word_problems: [
    {
      id: 'hkdse2020_word_problems_01',
      problem: "In a recruitment activity, the number of male applicants is $28\\%$ more than the number of female applicants. The difference between the number of male applicants and the number of female applicants is $91$. Find the number of male applicants in this recruitment activity.",
      problemType: "word_problems_percentage",
      difficulty: "intermediate",
      description: "Solving percentage problems with word problems",
      graphData: {
        nodes: [
          {
            id: "percentage_relationship",
            name: "Percentage Relationship",
            type: "concepts",
            description: "Male applicants 28% more than female",
            value: "Males = Females × 1.28",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "difference_given",
            name: "Difference Given",
            type: "concepts",
            description: "Difference between male and female applicants",
            value: "Males - Females = 91",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "system_equations",
            name: "System of Equations",
            type: "methods",
            description: "Setting up equations to solve",
            value: "M = 1.28F, M - F = 91",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "male_applicants",
            name: "Male Applicants",
            type: "variables",
            description: "Number of male applicants to find",
            value: "M",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "percentage_relationship",
            to: "system_equations",
            type: "provides_equation",
            strength: 9
          },
          {
            from: "difference_given",
            to: "system_equations",
            type: "provides_equation",
            strength: 9
          },
          {
            from: "system_equations",
            to: "male_applicants",
            type: "solves_for",
            strength: 10
          }
        ]
      }
    }
  ],
  inequalities: [
    {
      id: 'hkdse2020_inequalities_01',
      problem: "Consider the compound inequality $3 - x > \\frac{7 - x}{2}$ or $5 + x > 4$ ...................... (*) (a) Solve (*). (b) Write down the greatest negative integer that satisfies (*).",
      problemType: "inequalities_compound",
      difficulty: "intermediate",
      description: "Solving compound inequalities with 'or'",
      graphData: {
        nodes: [
          {
            id: "first_inequality",
            name: "First Inequality",
            type: "concepts",
            description: "3 - x > (7 - x)/2",
            value: "3 - x > (7 - x)/2",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "second_inequality",
            name: "Second Inequality",
            type: "concepts",
            description: "5 + x > 4",
            value: "5 + x > 4",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "compound_inequality",
            name: "Compound Inequality",
            type: "concepts",
            description: "Inequality with 'or' condition",
            value: "First OR Second",
            importance: 9,
            x: 0, y: 0, z: 0
          },
          {
            id: "greatest_negative",
            name: "Greatest Negative Integer",
            type: "concepts",
            description: "Largest negative integer in solution set",
            value: "Find largest negative integer satisfying (*)",
            importance: 7,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "first_inequality",
            to: "compound_inequality",
            type: "part_of",
            strength: 9
          },
          {
            from: "second_inequality",
            to: "compound_inequality",
            type: "part_of",
            strength: 8
          },
          {
            from: "compound_inequality",
            to: "greatest_negative",
            type: "constrains",
            strength: 7
          }
        ]
      }
    }
  ],
  quadratic: [
    {
      id: 'hkdse2020_quadratic_01',
      problem: "Let $\\text{p}(x) = 4x^2 + 12x + c$, where $c$ is a constant. The equation $\\text{p}(x) = 0$ has equal roots. Find (a) $c$; (b) the $x$-intercept of the graph of $y = \\text{p}(x) - 169$.",
      problemType: "quadratic_equal_roots",
      difficulty: "intermediate",
      description: "Working with quadratic equations with equal roots",
      graphData: {
        nodes: [
          {
            id: "quadratic_function",
            name: "Quadratic Function",
            type: "concepts",
            description: "p(x) = 4x² + 12x + c",
            value: "p(x) = 4x² + 12x + c",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "equal_roots",
            name: "Equal Roots",
            type: "concepts",
            description: "Discriminant = 0 for equal roots",
            value: "b² - 4ac = 0",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "discriminant_calculation",
            name: "Discriminant Calculation",
            type: "methods",
            description: "Calculating discriminant to find c",
            value: "12² - 4(4)(c) = 0",
            importance: 9,
            x: 200, y: 0, z: 0
          },
          {
            id: "x_intercept",
            name: "X-Intercept",
            type: "concepts",
            description: "X-intercept of translated function",
            value: "p(x) - 169 = 0",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "quadratic_function",
            to: "equal_roots",
            type: "has_property",
            strength: 9
          },
          {
            from: "equal_roots",
            to: "discriminant_calculation",
            type: "determines",
            strength: 9
          },
          {
            from: "quadratic_function",
            to: "x_intercept",
            type: "transformed_to",
            strength: 8
          }
        ]
      }
    }
  ]
}; 